import { useCallback, useEffect, useRef, useState } from 'react'
import { Track, type LocalVideoTrack } from 'livekit-client'
import { useLocalParticipant } from '@livekit/components-react'

import { canAutoEnableSegmentation, deviceClassLabel } from './deviceClass'
import { report } from './diagnostics'
import { senderFramesLookLive } from './publishHealth'
import { BRAND_PAD_COLOR, safeAreaBackgroundUrl } from './backgroundAsset'

/**
 * Background blur / virtual background for the local camera.
 *
 * This is a safeguarding feature more than a cosmetic one: our students are
 * children joining from bedrooms and living rooms, and without it their home,
 * their siblings and their belongings are broadcast to a class AND captured in a
 * recording we retain. That framing drives the defaults below.
 *
 * Three constraints shape the implementation:
 *
 * 1. It is not free. Segmentation runs per frame on the device, and on a low-end
 *    Android that CPU competes with video encode — the thing that must not
 *    degrade. So the default is device-aware rather than simply "on".
 * 2. The runtime is ~2.7 MB gzipped, fetched when someone chooses an effect.
 *    Enabling it is therefore a deliberate act with a visible loading state,
 *    never a silent stall, and the choice is remembered so they pay that cost
 *    once.
 * 3. Switching modes must go through `switchTo` on a processor that is already
 *    attached; attaching and detaching produces visible artefacts (the upstream
 *    docs call this out specifically).
 */

/**
 * The catalogue of virtual backgrounds.
 *
 * Deliberately five, not twenty. Every extra tile is another decision for a
 * ten-year-old to make in the thirty seconds before their lesson starts, and the
 * picker has to stay glanceable on a 360px phone. They are soft and abstract by
 * design — a background that competes with a child's face is a worse background,
 * however impressive it looks in a screenshot.
 *
 * Generated by scripts/make-backgrounds.mjs and served from our own origin: the
 * CSP allows no third-party image host, and a stock photo would bring a licensing
 * question into a product used by schools. `brand` is the one exception — the
 * official iSchool wall (marketing-provided asset, not generated).
 */
export const BACKGROUND_IMAGES = [
  { id: 'brand', labelKey: 'bg.brand' },
  { id: 'study', labelKey: 'bg.study' },
  { id: 'sky', labelKey: 'bg.sky' },
  { id: 'mint', labelKey: 'bg.mint' },
  { id: 'space', labelKey: 'bg.space' },
] as const

export type BackgroundImageId = (typeof BACKGROUND_IMAGES)[number]['id']
export type BackgroundMode = 'off' | 'blur' | BackgroundImageId

const IMAGE_IDS: readonly string[] = BACKGROUND_IMAGES.map((image) => image.id)

export function isBackgroundImage(mode: BackgroundMode): mode is BackgroundImageId {
  return IMAGE_IDS.includes(mode)
}

/** Full frame, composited behind the participant. */
export const backgroundImagePath = (id: BackgroundImageId) => `/backgrounds/${id}.jpg`
/** ~2 KB tile for the picker — six full frames to draw a menu would be absurd. */
export const backgroundThumbPath = (id: BackgroundImageId) => `/backgrounds/${id}-thumb.jpg`

/**
 * Backgrounds whose whole frame must stay visible (LK-025): the brand wall
 * carries the marketing logo edge to edge, and the processor's cover-crop
 * takes the edges whenever the camera aspect differs from the asset's.
 */
const SAFE_AREA_BACKGROUNDS: ReadonlySet<BackgroundImageId> = new Set(['brand'])

/**
 * The path to hand the processor for `mode`: the plain asset, except for
 * safe-area backgrounds, which are letterboxed to the LIVE camera aspect
 * first (see backgroundAsset.ts for why the pad must precede the processor).
 * Falls back to the plain path whenever the pad cannot be made — the cover
 * behaviour, not an error.
 */
async function safeAreaImagePath(mode: BackgroundImageId, track: LocalVideoTrack): Promise<string> {
  const path = backgroundImagePath(mode)
  if (!SAFE_AREA_BACKGROUNDS.has(mode)) return path
  const { width, height } = track.mediaStreamTrack.getSettings()
  if (!width || !height) return path
  const padded = await safeAreaBackgroundUrl(path, width, height, BRAND_PAD_COLOR).catch(() => null)
  return padded ?? path
}
/** Reported so the UI can explain a slow start or a failure rather than just spin. */
export type BackgroundStatus = 'idle' | 'loading' | 'active' | 'error'

const STORAGE_KEY = 'ischool-meet:background'
const BLUR_RADIUS = 12

/** Self-hosted — see scripts/copy-mediapipe.mjs for why these are not CDN URLs. */
const ASSET_PATHS = {
  tasksVisionFileSet: '/mediapipe/wasm',
  modelAssetPath: '/mediapipe/selfie_segmenter.tflite',
}

interface Support {
  supported: boolean
  /** The efficient MediaStreamTrackProcessor path rather than a canvas fallback. */
  modern: boolean
}

let cachedSupport: Support | null = null

async function detectSupport(): Promise<Support> {
  if (cachedSupport) return cachedSupport
  try {
    const mod = await import('@livekit/track-processors')
    cachedSupport = {
      supported: mod.supportsBackgroundProcessors(),
      modern: mod.supportsModernBackgroundProcessors(),
    }
  } catch {
    cachedSupport = { supported: false, modern: false }
  }
  return cachedSupport
}

/**
 * Blur is OFF for everyone by default — tutors and students alike.
 *
 * It used to switch itself on wherever the device looked capable enough (fast
 * connection, efficient processing path, desktop-sized viewport), which meant a
 * tutor on a laptop joined every lesson already blurred without asking for it.
 * That is the wrong default twice over: a class is a room people are meant to see
 * each other in, and segmentation is the single most expensive thing this app can
 * do to a CPU that is also encoding and decoding video.
 *
 * It stays one tap away, and a stored choice below still wins — so anyone who
 * wants it keeps it between lessons.
 */
function storedPreference(): BackgroundMode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'blur' || raw === 'off') return raw
    // An id from a previous release that no longer exists must not resurrect a
    // 404 image every time this student joins.
    return raw && IMAGE_IDS.includes(raw) ? (raw as BackgroundImageId) : null
  } catch {
    return null
  }
}

/** Single writer for the stored choice — used by `select` and by the failure path. */
function persistMode(mode: BackgroundMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    /* preference just will not persist */
  }
}

/**
 * `defaultMode` is what a participant gets when they have never chosen anything
 * on this device. Classroom supplies 'off' for every role, while a stored choice
 * ALWAYS wins over that default. And like stored choices, a default that needs
 * segmentation only applies where the efficient processing path exists — never
 * on the canvas fallback.
 */
export function useBackgroundEffect(
  credential: { code?: string | null; reconnectToken?: string | null },
  defaultMode: BackgroundMode = 'off',
  deferStoredMode = false,
) {
  const { cameraTrack } = useLocalParticipant()
  const [support, setSupport] = useState<Support | null>(null)
  const [mode, setMode] = useState<BackgroundMode>('off')
  const [deferredMode, setDeferredMode] = useState<BackgroundMode | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const processorRef = useRef<Awaited<ReturnType<typeof createProcessor>> | null>(null)
  const appliedRef = useRef(false)
  /** The exact track the processor is attached to, so a replacement re-attaches. */
  const attachedTrackRef = useRef<LocalVideoTrack | null>(null)
  /** Bumped per apply; every await checkpoint bails if it is no longer the latest. */
  const applyGenerationRef = useRef(0)
  /** Serialises applies so two switchTo calls never overlap on one processor. */
  const applyQueueRef = useRef<Promise<unknown>>(Promise.resolve())
  /** Shared so a first-enable race constructs the processor exactly once. */
  const processorPromiseRef = useRef<ReturnType<typeof createProcessor> | null>(null)
  /**
   * Telemetry credential kept in a ref rather than in the effect's deps.
   *
   * It was a dependency, and the effect calls setBusy — so any caller passing an
   * inline object (the natural thing to write) got: apply → setBusy → re-render →
   * new object identity → apply again, forever. A stress test caught it running
   * segmentation 33 times for a single tap. Today's caller memoises the object so
   * production never span, but that is a property of the CALLER, and this hook
   * should not be one refactor away from pinning a child's phone at 100% CPU.
   */
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  /**
   * In a ref for the same reason as the credential: the role (and therefore the
   * default) cannot change mid-connection, and making it a dependency would let
   * a re-render re-run the boot effect and stomp a choice the user made two
   * minutes into the lesson.
   */
  const defaultModeRef = useRef(defaultMode)
  const deferStoredModeRef = useRef(deferStoredMode)
  const deferredModeRef = useRef<BackgroundMode | null>(null)
  /**
   * Whether the CURRENT mode was chosen by a person (tap, or restored from a
   * previous tap) rather than supplied as the role default. The failure path
   * below needs the distinction: a broken user choice must be persisted away
   * or it re-breaks on every join, but a default that failed on a flaky first
   * fetch must stay a default — persisting 'off' there would silently opt a
   * tutor out of the branded wall forever over one bad network moment.
   */
  const userChoseRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    void detectSupport().then((result) => {
      if (cancelled) return
      setSupport(result)
      // A stored choice is honoured only where the efficient path exists. The
      // same family phone may be shared, and 'blur' saved on a capable device
      // must not switch on canvas-fallback segmentation for a weaker one. The
      // role default is held to the same bar — deliberately NOT persisted, so
      // it stays a default rather than becoming a phantom "choice" that would
      // survive a future change of the default.
      const stored = result.modern ? storedPreference() : null
      // A stored effect is not a boot-time mandate: it must pass the same
      // device qualification as a role default before it can consume encoder
      // headroom. The picker remains an explicit user choice. See deviceClass.ts.
      const defaultAllowed = result.modern && canAutoEnableSegmentation()
      const storedNeedsSegmentation = stored !== null && stored !== 'off'
      if (storedNeedsSegmentation && !defaultAllowed) {
        // Keep the preference: the picker still lets the tutor deliberately
        // choose it, but a stored effect must not spend encoder headroom unseen.
        userChoseRef.current = false
        deferredModeRef.current = null
        setDeferredMode(null)
        setMode('off')
        return
      }
      userChoseRef.current = stored != null
      if (storedNeedsSegmentation && deferStoredModeRef.current) {
        // Hosts join with plain video first. A saved effect is re-applied only
        // after its sender proves that the camera is already flowing.
        deferredModeRef.current = stored
        setDeferredMode(stored)
        setMode('off')
        return
      }
      setMode(stored ?? (defaultAllowed ? defaultModeRef.current : 'off'))
      if (result.modern && !defaultAllowed && defaultModeRef.current !== 'off') {
        report(credentialRef.current, {
          stage: 'permissions',
          permission: `background=default-withheld,${deviceClassLabel()}`,
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const track = cameraTrack?.track as LocalVideoTrack | undefined

  useEffect(() => {
    const desired = deferredMode
    if (desired === null || !track || track.source !== Track.Source.Camera || !support?.supported) return

    let cancelled = false
    let checking = false
    const checkSender = async () => {
      if (checking) return
      checking = true
      try {
        const frameCount = async () =>
          (await track.getSenderStats()).reduce((total, layer) => total + (layer.framesSent ?? Number.NaN), 0)
        const before = await frameCount()
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000))
        const after = await frameCount()
        if (!cancelled && deferredModeRef.current === desired && senderFramesLookLive(before, after)) {
          deferredModeRef.current = null
          setDeferredMode(null)
          setMode(desired)
        }
      } catch {
        /* sender evidence is advisory; retry while the saved mode remains desired */
      } finally {
        checking = false
      }
    }

    void checkSender()
    const timer = window.setInterval(() => void checkSender(), 2500)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [deferredMode, support, track])

  /**
   * Applying the desired mode is separated from choosing it, so a track that
   * arrives late (camera switched on mid-class) still picks up the preference.
   *
   * Two things make this safe that a plain `cancelled` flag did not:
   *
   * 1. **A generation counter checked after EVERY await.** A child tapping
   *    through backgrounds is the expected path, not an edge case, and each tap
   *    re-runs this effect. With a single check at the top, an older run could
   *    still be inside `switchTo(sky)` when a newer one finished `switchTo(mint)`
   *    — and whichever settled last won the video, leaving the camera on Sky
   *    while the tick, the state and localStorage all said Mint.
   * 2. **A serialising queue.** Even with the guard, two `switchTo` calls could
   *    otherwise be in flight on the same processor at once. Chaining every apply
   *    onto the previous one means the library only ever sees one at a time, and
   *    a superseded run drops out at its first checkpoint without touching it.
   */
  useEffect(() => {
    if (!track || track.source !== Track.Source.Camera || !support?.supported) return
    // A new LocalVideoTrack (camera off→on, device change, republish) has never
    // had the processor attached, whatever we did to the previous one. Without
    // this the attach was skipped and `switchTo` ran against a processor bound to
    // a dead track, so the effect silently vanished for the rest of the lesson.
    if (attachedTrackRef.current !== track) appliedRef.current = false
    if (mode === 'off' && !appliedRef.current) return // nothing attached, nothing to do

    const generation = applyGenerationRef.current + 1
    applyGenerationRef.current = generation
    const current = () => applyGenerationRef.current === generation

    async function apply() {
      if (!current()) return
      setBusy(true)
      setFailed(false)
      try {
        // One shared promise, so two taps racing the very first enable cannot
        // each construct a ~2.7 MB processor and orphan one of them.
        if (!processorPromiseRef.current) processorPromiseRef.current = createProcessor()
        const processor = await processorPromiseRef.current
        if (!current() || !track) return
        processorRef.current = processor

        if (!appliedRef.current) {
          await track.setProcessor(processor)
          if (!current()) return
          appliedRef.current = true
          attachedTrackRef.current = track
        }
        // switchTo rather than re-attaching: attaching and detaching a processor
        // is what produces the visible flash.
        //
        // LK-025: safe-area backgrounds (the brand wall — its logo must never be
        // cover-cropped) are pre-padded to the CAMERA's own aspect before the
        // processor sees them, so its cover mapping is a no-op and the whole
        // asset stays visible. Other backgrounds are full-frame gradients where
        // cover is the intended look.
        const imagePath = isBackgroundImage(mode) ? await safeAreaImagePath(mode, track) : undefined
        await processor.switchTo(
          mode === 'blur'
            ? { mode: 'background-blur', blurRadius: BLUR_RADIUS }
            : imagePath
              ? { mode: 'virtual-background', imagePath }
              : { mode: 'disabled' },
        )
        if (!current()) return
        report(credentialRef.current, {
          stage: 'permissions',
          outcome: 'ok',
          permission: `background=${mode}${mode === 'brand' ? `,${deviceClassLabel()}` : ''}`,
        })
      } catch (error) {
        if (current()) {
          // Never leave the camera broken because an effect failed: fall back to
          // plain video and tell the funnel about it.
          try {
            await track?.stopProcessor()
          } catch {
            /* ignore */
          }
          appliedRef.current = false
          attachedTrackRef.current = null
          setFailed(true)
          // Persisted, not just set in state — but only for a mode a PERSON
          // chose: a missing or undecodable image would otherwise be restored
          // from storage on every future join and fail again. A failed role
          // DEFAULT is the opposite case — it was never stored, so writing
          // 'off' here would convert one flaky fetch into a permanent opt-out;
          // leaving storage empty lets the default try again next lesson.
          if (userChoseRef.current) persistMode('off')
          setMode('off')
          const caught = error as Error | undefined
          const errorName = caught?.name ?? 'unknown'
          const errorDetail =
            errorName === 'TypeError'
              ? `TypeError:${(caught?.message ?? 'unknown').replace(/[\r\n,]/g, ' ').slice(0, 80)}`
              : errorName
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            errorKind: 'background_processor_failed',
            // NAMESPACED, not a bare exception name.
            //
            // This used to emit `String(error.name)` on its own, so the
            // `permission` field — where every other value is a device verdict
            // (`mic=ok`, `camera=granted`, `krisp=on`, `recover=…`) — would
            // suddenly contain `TypeError`. No query can tell that apart from a
            // device state, and on 2026-08-03 it cost exactly the confusion you
            // would predict: one background-processor failure on session 3885011
            // was read as two separate findings, a device-permission anomaly and
            // a processor error, by someone reviewing the tally.
            //
            // Also carries the device class, because the interesting question
            // about a processor that threw is what it was running on.
            permission: `background=failed,error=${errorDetail},${deviceClassLabel()}`,
          })
        }
      } finally {
        if (current()) setBusy(false)
      }
    }

    // Chained, never fired bare: this is what guarantees the library sees one
    // switchTo at a time. Both arms are `apply` so a rejected predecessor still
    // lets the next run proceed.
    applyQueueRef.current = applyQueueRef.current.then(apply, apply)
    // `credential` is deliberately NOT a dependency — see credentialRef above.
  }, [track, mode, support])

  const select = useCallback((next: BackgroundMode) => {
    deferredModeRef.current = null
    setDeferredMode(null)
    userChoseRef.current = true
    setFailed(false)
    setMode(next)
    persistMode(next)
  }, [])

  /**
   * Warms an image before it is chosen.
   *
   * Called when a tile is hovered or focused. Switching the processor to an image
   * that is not in cache yet stalls the video for as long as the fetch takes, and
   * on a congested Cairo connection that is the difference between "instant" and
   * "did it break?". The images are ~20 KB, so speculatively fetching one the
   * user is pointing at costs nothing.
   */
  const prefetch = useCallback((id: BackgroundMode) => {
    if (!isBackgroundImage(id)) return
    const image = new Image()
    image.src = backgroundImagePath(id)
  }, [])

  const status: BackgroundStatus = failed
    ? 'error'
    : busy
      ? 'loading'
      : mode !== 'off'
        ? 'active'
        : 'idle'

  return {
    mode,
    select,
    prefetch,
    busy,
    status,
    available: Boolean(support?.supported),
    /** True when only the slow canvas fallback exists — worth warning about. */
    degraded: Boolean(support?.supported && !support.modern),
  }
}

async function createProcessor() {
  const { BackgroundProcessor } = await import('@livekit/track-processors')
  // Constructed in 'disabled' mode: the upstream guidance is to attach once and
  // switch, so the first enable does not flash.
  return BackgroundProcessor({ mode: 'disabled', assetPaths: ASSET_PATHS })
}
